/**
 * MafiaInstructions — How to Play overlay
 *
 * Displays role descriptions, phase explanations, win conditions,
 * strategy tips, and Extended Mode info.
 *
 * Requirements: 31.1–31.8, 31.11
 */

import React, { useEffect, useRef } from 'react';
import { ROLE_INFO } from '../types';
import './MafiaInstructions.css';

export interface MafiaInstructionsProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MafiaInstructions: React.FC<MafiaInstructionsProps> = ({ isOpen, onClose }) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  if (!isOpen) return null;

  const roles = [ROLE_INFO.villager, ROLE_INFO.mafia, ROLE_INFO.doctor, ROLE_INFO.detective];

  return (
    <div
      className="mafia-instructions-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="How to Play Mafia"
    >
      <div className="mafia-instructions">
        <button
          className="mafia-instructions__close"
          onClick={onClose}
          aria-label="Close instructions"
        >
          ✕
        </button>

        <h2 className="mafia-instructions__title">📖 How to Play Mafia</h2>

        <div className="mafia-instructions__section">
          <h3>🎯 Objective</h3>
          <p>
            Mafia is a social deduction game. The Village team must identify and
            eliminate all Mafia members through discussion and voting. The Mafia
            team must eliminate enough Villagers to gain control.
          </p>
        </div>

        <div className="mafia-instructions__section">
          <h3>👥 Roles</h3>
          <ul className="mafia-instructions__role-list">
            {roles.map((role) => (
              <li key={role.role} className="mafia-instructions__role-item">
                <span className="mafia-instructions__role-icon">{role.icon}</span>
                <div>
                  <div className="mafia-instructions__role-name">{role.name}</div>
                  <div className="mafia-instructions__role-team">
                    Team: {role.team === 'village' ? 'Village' : 'Mafia'}
                  </div>
                  <div className="mafia-instructions__role-desc">{role.description}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mafia-instructions__section">
          <h3>🔄 Game Phases</h3>
          <ul className="mafia-instructions__phase-list">
            <li className="mafia-instructions__phase-item">
              <strong>🌙 Night Phase:</strong> Mafia secretly votes to eliminate a player.
              The Doctor chooses someone to protect. The Detective investigates one player.
              Villagers sleep.
            </li>
            <li className="mafia-instructions__phase-item">
              <strong>☀️ Day Discussion:</strong> All alive players discuss who they suspect.
              Share information, make accusations, and defend yourself.
            </li>
            <li className="mafia-instructions__phase-item">
              <strong>🗳️ Day Voting:</strong> Players vote to eliminate a suspect.
              A strict majority is needed. Eliminated player's role is revealed.
            </li>
          </ul>
        </div>

        <div className="mafia-instructions__section">
          <h3>🏆 Win Conditions</h3>
          <ul className="mafia-instructions__win-conditions">
            <li><strong>Village wins:</strong> All Mafia members are eliminated.</li>
            <li><strong>Mafia wins:</strong> Mafia members equal or outnumber non-Mafia players.</li>
          </ul>
        </div>

        <div className="mafia-instructions__section">
          <h3>💡 Strategy Tips</h3>
          <ul className="mafia-instructions__tips">
            <li>🏘️ <strong>Villager:</strong> Pay attention to voting patterns and who defends whom.</li>
            <li>🔪 <strong>Mafia:</strong> Blend in, cast suspicion on others, coordinate with teammates.</li>
            <li>💊 <strong>Doctor:</strong> Vary your protection targets. Don't protect the same person twice in a row.</li>
            <li>🔍 <strong>Detective:</strong> Share findings carefully — revealing too early makes you a target.</li>
          </ul>
        </div>

        <div className="mafia-instructions__section">
          <h3>📏 Extended Mode</h3>
          <p>
            For larger groups of 16–30 players, Extended Mode adds more Mafia members,
            additional Doctors and Detectives, creating deeper strategic gameplay.
            The host can enable Extended Mode in the lobby settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MafiaInstructions;
